import { isEqual } from 'es-toolkit/compat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { DebugLevel } from '../../_configs/types';
import { standardFilter } from '../../_flux/defaults';
import { mockupCarList } from '../../_json/CarList';
import getCarList from '../../_tools/axios/cars/getCarList';
import { type Filter } from '../../_types/Car';
import { useAllFacetsContext } from '../../context/all-facets';
import { useSettingsContext } from '../../context/settings';
import { Modules } from '../../modules/_definition/types';
import getUrlFromFilter from '../../modules/fahrzeugsuche/filter/utils/getUrlFromFilter';
import getSearchString from '../../utils/texte/getSearchString';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import enhancedNavigate from '../../utils/tools/enhancedNavigate';
import scrollTo from '../../utils/tools/scrollTo';
import { useLocalStorage, useSessionStorage } from '../use-storage';
import useLocales from '../useLocales';
import buildNewFilter from './buildNewFilter';
import { type RegisterFormReturn, handleListData, registerForm } from './func';
import removeNonFilterPropsFromFilter from './removeNonFilterPropsFromFilter';
import { type UseCarSearchForm, type UseCarSearchProps, type UseCarSearchReturn } from './types';

const abortController: Partial<Record<Modules, AbortController>> = {};
const moduleMethods: Partial<Record<Modules, RegisterFormReturn<UseCarSearchForm>>> = {};
const timerDelayUseEffectValues: Partial<Record<Modules, NodeJS.Timeout>> = {};
const timerDelayLoading: Partial<Record<Modules, NodeJS.Timeout>> = {};

export default function useCarSearch(props: UseCarSearchProps): UseCarSearchReturn {
    const { allFaarenVehicles, disableAutoLoad, isContact, module: moduleProp, navigate, params, query } = props;
    const allFacets = useAllFacetsContext();
    const { currentLang, translate } = useLocales();
    const settings = useSettingsContext();
    const { state: lastViewedCar, update: setLastViewedCar } = useSessionStorage('lastViewedCar');
    const { state: listMode, update: setListMode } = useLocalStorage('listMode');
    const { state: sessionSearch, update: setSessionSearch } = useSessionStorage('search');

    const [initialized, setInitialized] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [results, setResults] = useState<UseCarSearchReturn['results']>({ cars: { 1: {}, 2: {}, 3: {}, 4: {}, 5: {}, 6: {}, 7: {}, 8: {} }, facets: {}, numFound: 0 });
    const module = useMemo<Modules>(() => (isContact ? Modules.Contact : moduleProp), [isContact, moduleProp]);
    const page = useRef<Filter['moreCars']>(1);
    const textSearch = useRef<Filter['freier_text']>('');

    moduleMethods[module] = registerForm<UseCarSearchForm>(standardFilter);
    const { control, handleSubmit, reset, setValue } = moduleMethods?.[module] || {};
    const values = useWatch({ control });

    async function getCarData(data: Filter, isPaging: boolean) {
        clearTimeout(timerDelayLoading?.[module]);
        timerDelayLoading[module] = setTimeout(() => {
            setLoading(true);
            clearTimeout(timerDelayLoading?.[module]);
        }, 500);

        abortController?.[module]?.abort();
        abortController[module] = new AbortController();

        const getCarListResult = settings?.general?.mockup
            ? { search: {}, service: undefined }
            : getCarList(settings?.general?.idFirma, data, currentLang?.value, settings?.general?.debug, module, settings, abortController?.[module]?.signal);

        const searchForOcto = getSearchString(getCarListResult?.search, allFacets, undefined, false, false, translate, settings, true);

        const result = settings?.general?.mockup ? mockupCarList : (await getCarListResult?.service)?.data;

        const newData = await handleListData({
            allFaarenVehicles,
            listServiceData: result,
            module,
            searchForOcto,
            settings,
            translate,
        });

        abortController[module] = null;
        setResults(newData);
        clearTimeout(timerDelayLoading?.[module]);
        setLoading(false);

        if ((module === Modules.Fahrzeugsuche
            || (module === Modules.Fahrzeugliste && settings?.[module]?.showCompleteList)
            || (module === Modules.Finanzierungsansicht && settings?.[module]?.showCompleteList)
        ) && (!settings?.fahrzeugsuche?.hideHeading || isPaging === true)) {
            const sessionLastViewedCar = lastViewedCar;
            if (!checkUndefinedNullOrEmpty(sessionLastViewedCar)) {
                scrollTo(`[data-carid="${sessionLastViewedCar}"]`, 'center');
                setLastViewedCar(undefined);
            } else if (isPaging === true && (module === Modules.Fahrzeugsuche && !settings?.fahrzeugsuche?.endlessScrolling)) {
                // Scroll to top only on normal fahrzeugsuche paging
                scrollTo(!checkUndefinedNullOrEmpty(location?.hash) ? `#${location?.hash?.replace('#', '')}` : `#${module}ScrollTop`, 'start');
            }
        }
    }

    function onSubmit(data: UseCarSearchForm, isPaging: boolean) {
        if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - onSubmit - data`, data);
            console.log(`${module} - onSubmit - isPaging`, isPaging);
        }

        // Reset to page 1 on new filter search
        if (!isPaging) {
            data.moreCars = 1;
        }

        if (!checkUndefinedOrNull(navigate)) {
            const newUrl = getUrlFromFilter(allFacets, settings, data, module, false);
            if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                console.log(`${module} - onSubmit - newUrl`, newUrl);
            }

            if (!newUrl.noPushToPage && location?.pathname !== '/' && location?.pathname?.startsWith(settings?.general?.searchPath)) {
                if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                    console.log(`${module} - onSubmit - navigate to`, newUrl?.url);
                }
                enhancedNavigate(newUrl?.url, navigate);
                return;
            }
        } else if (module === Modules.Fahrzeugliste || module === Modules.Finanzierungsansicht) {
            // endless loading mode: liste / fin have no router and no navigate
            const newUrl = new URL(location?.href);
            if (data?.moreCars > standardFilter?.moreCars) {
                newUrl.searchParams.set('moreCars', data?.moreCars?.toString());
            }

            if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                console.log(`${module} - onSubmit - newUrl`, newUrl);
            }

            if (newUrl) {
                history.replaceState(null, '', newUrl);
            }
        }

        if ((module === Modules.Fahrzeugsuche
            || (module === Modules.Fahrzeugliste && settings?.[module]?.showCompleteList)
            || (module === Modules.Finanzierungsansicht && settings?.[module]?.showCompleteList)
        ) && !settings?.general?.mockup) {
            const newSessionSearch: Filter = {};
            Object.keys(standardFilter)
                ?.filter((key) => !isEqual(standardFilter?.[key], data?.[key]))
                ?.map((key) => (newSessionSearch[key] = data?.[key]));
            setSessionSearch(newSessionSearch);
        }

        if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - onSubmit - call getCarData`);
        }

        getCarData(data, isPaging);
        page.current = data?.moreCars;
        textSearch.current = data?.freier_text;
    }

    useEffect(() => {
        if (!initialized || module === Modules.Fahrzeugsuche) { // DEV (module === Modules.Fahrzeugsuche || (module === Modules.Fahrzeugliste && settings?.fahrzeugliste?.showCompleteList))
            const newFilter = buildNewFilter({
                allFacets,
                initialized,
                module,
                params,
                query,
                reset: false,
                sessionSearch,
                settings,
                translate,
            });
            if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                console.log(`${module} - useEffect params, query - params`, params);
                console.log(`${module} - useEffect params, query - query`, query?.toString());
                console.log(`${module} - useEffect params, query - newFilter`, newFilter);
            }
            reset(newFilter);
            if (!initialized) {
                setInitialized(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, query]);

    useEffect(() => {
        if (!disableAutoLoad) {
            clearTimeout(timerDelayUseEffectValues?.[module]);
            timerDelayUseEffectValues[module] = setTimeout(
                async () => {
                    if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                        console.log(`${module} - useEffect values - values`, values);
                        console.log(`${module} - useEffect values - page?.current`, page?.current);
                        console.log(`${module} - useEffect values - textSearch?.current`, textSearch?.current);
                        console.log(`${module} - useEffect values - delay`, textSearch?.current !== values?.freier_text ? 350 : 150);
                    }
                    onSubmit(values, page?.current !== values?.moreCars);
                },
                textSearch?.current !== values?.freier_text ? 350 : 150
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableAutoLoad, values]);

    const canReset = useMemo(() => {
        const standardFilterWithoutNonFilter = removeNonFilterPropsFromFilter(standardFilter, settings?.general?.useFirmenFilter, settings?.[module]?.forcedFilter);
        const valuesWithoutNonFilter = removeNonFilterPropsFromFilter(values, settings?.general?.useFirmenFilter, settings?.[module]?.forcedFilter);
        return !isEqual(standardFilterWithoutNonFilter, valuesWithoutNonFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    return {
        canReset,
        formKey: `${module}_FormProvider`,
        handleSubmit: handleSubmit((data) => onSubmit(data, page?.current !== data?.moreCars)),
        listMode,
        loading,
        methods: moduleMethods?.[module]?.methods,
        notFound: !loading && checkUndefinedNullOrEmpty(results?.cars),
        reset() {
            const newFilter = buildNewFilter({
                allFacets,
                initialized,
                module,
                params: {},
                query: new URLSearchParams(),
                reset: true,
                sessionSearch: { sort: sessionSearch?.sort, order: sessionSearch?.order },
                settings,
                translate,
            });
            if (settings?.general?.debug === DebugLevel.CARS || settings?.general?.debug >= DebugLevel.INFO) {
                console.log(`${module} - reset - newFilter`, newFilter);
            }
            reset(newFilter);
        },
        results: {
            cars: results?.cars || {},
            facets: results?.facets || {},
            numFound: results?.numFound || 0,
        },
        setListMode,
        setValue,
        values,
    };
}
